import styled from '@emotion/styled';
import Link from 'next/link';
import React, { DOMAttributes } from 'react';
import { useMediaQuery } from '../../../../../utils/hooks/useMediaQuery';
import { breakpointQueries, breakpoints, colors, spacing } from '../../../../../utils/styleguide';
import { HandleNavItemSelected } from '../../../../contentful-elements/navigationbar/common';
import Icon from '../../../Icon';
import Typography from '../../../text/Typography';
import { NavLinkItemType } from '../../navTypes';

const LinkIcon = styled(Icon)`
  margin: 0 ${spacing[0]}px;
  &:hover {
    color: ${colors.black};
  }
`;

function TitleLink({
  title,
  link,
  ...props
}: {
  title: string;
  link?: string;
} & DOMAttributes<HTMLAnchorElement>) {
  return (
    <Link href={link || '/'} passHref>
      <a {...props}>{title}</a>
    </Link>
  );
}

const LinkGroupsWrapper = styled.div`
  margin-bottom: ${spacing[4]}px;
  ${breakpoints.desktop} {
    display: flex;
    justify-content: space-between;
    margin: 0 ${spacing[4]}px;
  }
`;

const LinkText = styled(Typography)`
  display: flex;
  align-items: center;
  ${({ color }: { color?: string }) => `color: ${color || colors.black};`}
  padding: ${spacing[4]}px 0;
  cursor: pointer;
  justify-content: space-between;
  ${breakpoints.desktop} {
    display: flex;
    align-items: center;
    padding: ${spacing[1]}px ${spacing[3]}px;
    cursor: pointer;
  }
  &:hover {
    color: ${colors.black};
  }
  & a {
    ${({ color }: { color?: string }) => `color: ${color || colors.black};`}
    text-decoration: none;
  }
`;

const Menu = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: start;
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
`;

type LinkGroupsProps = {
  textColor?: string;
  linkGroups?: NavLinkItemType[];
  handleNavItemClick: HandleNavItemSelected;
};

const LinkGroups = ({ linkGroups, handleNavItemClick, textColor }: LinkGroupsProps) => {
  const [isDesktop] = useMediaQuery(breakpointQueries.desktop);
  return (
    <LinkGroupsWrapper>
      {linkGroups?.map(({ titleLink, title, id }) => (
        <LinkText
          key={id}
          color={textColor}
          variant={isDesktop ? 'buttonSmall' : 'buttonLarge'}
          tag="div"
        >
          {titleLink ? (
            <TitleLink
              title={title}
              link={titleLink}
              onMouseEnter={() => handleNavItemClick({ id, type: 'focus' })}
              onClick={() => handleNavItemClick({ id, type: 'click' })}
            />
          ) : (
            <Menu
              onMouseEnter={() => handleNavItemClick({ id, type: 'focus' })}
              onClick={() => handleNavItemClick({ id, type: 'click' })}
            >
              {title}
              <LinkIcon name="chevron-s-down-outline" size={14} stroke={textColor} />
            </Menu>
          )}
        </LinkText>
      ))}
    </LinkGroupsWrapper>
  );
};

export default React.memo(LinkGroups);
